/**
 * PII Access Request API — frontend helper for requesting temporary
 * access to customer contact data.
 */

const isLocalDev = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const BPM_BASE_URL = isLocalDev 
  ? 'http://localhost:8084'
  : (process.env.REACT_APP_BUSINESS_PROCESS_SERVICE_URL || 'https://erp.escooterlab.ch/business-process-management');

const BPM_API = `${BPM_BASE_URL.replace(/\/+$/, '')}/api/v1`;

const authHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('accessToken') || '';
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

/**
 * Check if the current user has active access to a customer's PII.
 * Returns { has_access, expires_at, remaining_seconds, pending_request_id, cooldown_until, cooldown_remaining_seconds }
 */
export const checkPiiAccess = async (customerActorId) => {
  const res = await fetch(`${BPM_API}/pii-access/check/${customerActorId}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`PII access check failed: ${res.status}`);
  return res.json();
};

/**
 * Create a new PII access request.
 */
export const requestPiiAccess = async ({ customerActorId, processId, reason }) => {
  const res = await fetch(`${BPM_API}/pii-access/request`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      customer_actor_id: customerActorId,
      process_id: processId || null,
      reason: reason || null,
    }),
  });
  if (res.status === 429) {
    const data = await res.json();
    throw new Error(data.detail || 'Request on cooldown');
  }
  if (res.status === 409) {
    const data = await res.json();
    throw new Error(data.detail || 'Request already exists');
  }
  if (!res.ok) throw new Error(`PII access request failed: ${res.status}`);
  return res.json();
};

/**
 * Get pending PII access request count (admin only).
 */
export const getPiiPendingCount = async () => {
  const res = await fetch(`${BPM_API}/pii-access/pending-count`, {
    headers: authHeaders(),
  });
  if (!res.ok) return { count: 0 };
  return res.json();
};

/**
 * List PII access requests (admin only).
 */
export const listPiiRequests = async (status = null) => {
  const url = status
    ? `${BPM_API}/pii-access/requests?status=${status}`
    : `${BPM_API}/pii-access/requests`;
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(`List PII requests failed: ${res.status}`);
  return res.json();
};

/**
 * Approve a PII access request (admin only).
 */
export const approvePiiRequest = async (requestId, durationMinutes = 60) => {
  const res = await fetch(`${BPM_API}/pii-access/${requestId}/approve`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({ access_duration_m: durationMinutes }),
  });
  if (!res.ok) throw new Error(`Approve failed: ${res.status}`);
  return res.json();
};

/**
 * Decline a PII access request (admin only).
 */
export const declinePiiRequest = async (requestId, { declineReason, cooldownHours = 24 } = {}) => {
  const res = await fetch(`${BPM_API}/pii-access/${requestId}/decline`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify({
      decline_reason: declineReason || null,
      cooldown_hours: cooldownHours,
    }),
  });
  if (!res.ok) throw new Error(`Decline failed: ${res.status}`);
  return res.json();
};
